/**
 * Transaction Offline Service
 *
 * Extends BaseOfflineService for transaction-specific operations.
 * Handles income/expense creation and transaction-specific filtering.
 */

import type { Table } from 'dexie';

import { offlineDb } from '../database/offlineDatabase';
import { BaseOfflineService } from './BaseOfflineService';
import type {
  EntityType,
  OfflineEntity,
  ApiServiceMethods,
} from '../types/offline.types';

// Import transaction types
import type { Transaction, UpdateTransactionInput } from '#/entity';
import type { PaginatedResponse, TransactionQueryFilters, TransactionStatsResponse } from '#/api';
import { TransactionType, PaymentMethod } from '@/types/enum';

// Import online service for API calls
import transactionService, {
  type CreateIncomeInput,
  type CreateExpenseInput,
} from '@/api/services/transactionService';

// ============================================
// TYPES
// ============================================

/**
 * Combined create input type
 */
export type CreateTransactionInput = CreateIncomeInput | CreateExpenseInput;

// ============================================
// OFFLINE SERVICE
// ============================================

/**
 * Transaction Offline Service
 * Provides offline-first transaction operations
 */
export class TransactionOfflineService extends BaseOfflineService<
  Transaction,
  CreateTransactionInput,
  UpdateTransactionInput
> {
  protected readonly entityType: EntityType = 'transaction';
  protected readonly table: Table<OfflineEntity<Transaction>, string> = offlineDb.transactions;

  protected readonly apiService: ApiServiceMethods<
    Transaction,
    CreateTransactionInput,
    UpdateTransactionInput
  > = {
    getAll: (filters) => transactionService.getAll(filters as TransactionQueryFilters),
    getOne: transactionService.getOne,
    create: async (data: CreateTransactionInput) => {
      // Determine if income or expense based on type or category
      if ('createReceivable' in data || 'receivableDueDate' in data) {
        return transactionService.createIncome(data as CreateIncomeInput);
      }
      if ('employeeId' in data || 'paidAmount' in data || 'createDebtForRemaining' in data) {
        return transactionService.createExpense(data as CreateExpenseInput);
      }
      // Default to income for backward compatibility
      return transactionService.createIncome(data as CreateIncomeInput);
    },
    update: transactionService.update,
    delete: transactionService.delete,
  };

  // ============================================
  // INCOME/EXPENSE SPECIFIC METHODS
  // ============================================

  /**
   * Create an income transaction
   */
  async createIncome(data: CreateIncomeInput): Promise<Transaction> {
    const inputWithType = {
      ...data,
      type: TransactionType.INCOME,
    };
    return this.create(inputWithType);
  }

  /**
   * Create an expense transaction
   */
  async createExpense(data: CreateExpenseInput): Promise<Transaction> {
    const inputWithType = {
      ...data,
      type: TransactionType.EXPENSE,
    };
    return this.create(inputWithType);
  }

  // ============================================
  // SUMMARY METHODS
  // ============================================

  /**
   * Get transaction summary (works offline with cached data)
   */
  async getSummary(filters?: TransactionQueryFilters): Promise<TransactionStatsResponse> {
    // If online, try to get from server
    if (navigator.onLine) {
      try {
        return await transactionService.getSummary(filters);
      } catch (error) {
        console.warn('[TransactionOffline] Failed to get summary from server, using cache:', error);
      }
    }

    // Calculate from cached data
    return this.calculateSummaryFromCache(filters);
  }

  /**
   * Calculate summary statistics from cached transactions
   */
  private async calculateSummaryFromCache(
    filters?: TransactionQueryFilters
  ): Promise<TransactionStatsResponse> {
    // Get all cached transactions
    let transactions = await this.table.toArray();

    // Apply filters
    if (filters) {
      if (filters.branchId) {
        transactions = transactions.filter((t) => t.branchId === filters.branchId);
      }
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        transactions = transactions.filter((t) => new Date(t.date) >= startDate);
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        transactions = transactions.filter((t) => new Date(t.date) <= endDate);
      }
    }

    // Calculate totals
    let incomeCash = 0;
    let incomeMaster = 0;
    let totalExpense = 0;

    for (const transaction of transactions) {
      if (transaction.type === TransactionType.INCOME) {
        if (transaction.paymentMethod === PaymentMethod.CASH) {
          incomeCash += transaction.amount;
        } else if (transaction.paymentMethod === PaymentMethod.MASTER) {
          incomeMaster += transaction.amount;
        }
      } else if (transaction.type === TransactionType.EXPENSE) {
        totalExpense += transaction.amount;
      }
    }

    const totalIncome = incomeCash + incomeMaster;
    const net = totalIncome - totalExpense;

    return {
      date: filters?.startDate ?? new Date().toISOString().split('T')[0],
      branchId: filters?.branchId ?? null,
      income_cash: incomeCash,
      income_master: incomeMaster,
      total_income: totalIncome,
      total_expense: totalExpense,
      net,
    };
  }

  // ============================================
  // FILTERING
  // ============================================

  /**
   * Get transactions by type
   */
  async getByType(type: TransactionType): Promise<Transaction[]> {
    const transactions = await this.table.where('type').equals(type).toArray();
    return transactions.map(this.stripOfflineMetadata);
  }

  /**
   * Get transactions by date range
   */
  async getByDateRange(startDate: string, endDate: string): Promise<Transaction[]> {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const transactions = await this.table
      .filter((t) => {
        const date = new Date(t.date);
        return date >= start && date <= end;
      })
      .toArray();

    return transactions.map(this.stripOfflineMetadata);
  }

  /**
   * Get transactions by branch
   */
  async getByBranch(branchId: string): Promise<Transaction[]> {
    const transactions = await this.table.where('branchId').equals(branchId).toArray();
    return transactions.map(this.stripOfflineMetadata);
  }

  /**
   * Override applyFilters for transaction-specific filtering
   */
  protected override applyFilters(
    items: OfflineEntity<Transaction>[],
    filters: Record<string, unknown>
  ): OfflineEntity<Transaction>[] {
    return items.filter((item) => {
      // Type filter
      if (filters.type && item.type !== filters.type) {
        return false;
      }

      // Category filter
      if (filters.categoryId && item.categoryId !== filters.categoryId) {
        return false;
      }

      // Payment method filter
      if (filters.paymentMethod && item.paymentMethod !== filters.paymentMethod) {
        return false;
      }

      // Branch filter
      if (filters.branchId && item.branchId !== filters.branchId) {
        return false;
      }

      // Date range filter
      if (filters.startDate) {
        const startDate = new Date(filters.startDate as string);
        if (new Date(item.date) < startDate) {
          return false;
        }
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate as string);
        if (new Date(item.date) > endDate) {
          return false;
        }
      }

      // Search filter (searches description and notes)
      if (filters.search) {
        const searchTerm = (filters.search as string).toLowerCase();
        const description = item.description?.toLowerCase() ?? '';
        const notes = item.notes?.toLowerCase() ?? '';
        if (!description.includes(searchTerm) && !notes.includes(searchTerm)) {
          return false;
        }
      }

      return true;
    });
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const transactionOfflineService = new TransactionOfflineService();
